/**
 * CBO Compliance Dashboard
 * Overview of all members' work requirement compliance for CBO staff
 * Includes verification queue (merged from CBOWorkVerificationQueue)
 * Trust Marketplace - Work Documentation System
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { authFetch } from '@/lib/authFetch';
import {
  Users,
  CheckCircle2,
  AlertTriangle,
  Clock,
  TrendingUp,
  TrendingDown,
  FileText,
  Calendar,
  Download,
  RefreshCw,
  XCircle,
  User,
  Briefcase
} from 'lucide-react';

interface CBODashboardProps {
  cboId: number;
  programId?: number;
}

interface DashboardData {
  cbo_id: number;
  program_id: number | null;
  total_members: number;
  compliant_count: number;
  compliance_rate: number;
  pending_verifications: number;
  at_risk_count: number;
  at_risk_members: Array<{
    enrollment_id: number;
    member_id: number;
    member_name: string;
    hours_submitted: number;
    hours_needed: number;
    days_remaining?: number;
    status?: string;
  }>;
  current_month: number;
  current_year: number;
}

interface MonthlyTrend {
  month: number;
  year: number;
  compliance_rate: number;
  total_members: number;
  compliant_count: number;
}

interface WorkSubmission {
  id: number;
  enrollment_id: number;
  member_id: number;
  member_name: string;
  month: number;
  year: number;
  total_hours: number;
  employment_hours: number;
  education_hours: number;
  volunteer_hours: number;
  job_training_hours: number;
  meets_requirement: boolean;
  verification_status: string;
  submitted_date: string | null;
  has_documents: boolean;
  document_count: number;
  employer_name: string | null;
  submission_method: string;
}

const CBOComplianceDashboard: React.FC<CBODashboardProps> = ({ cboId, programId }) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [trends, setTrends] = useState<MonthlyTrend[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedProgram, setSelectedProgram] = useState<number | null>(programId || null);

  // Tab state: 'overview' or 'verification'
  const [activeTab, setActiveTab] = useState<'overview' | 'verification'>('overview');

  // Verification queue state
  const [submissions, setSubmissions] = useState<WorkSubmission[]>([]);
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueFilter, setQueueFilter] = useState<string>('PENDING');
  const [selectedSubmission, setSelectedSubmission] = useState<WorkSubmission | null>(null);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, [cboId, selectedProgram]);

  // Fetch verification queue when switching to that tab or changing filter
  useEffect(() => {
    if (activeTab === 'verification') {
      fetchVerificationQueue();
    }
  }, [activeTab, queueFilter, cboId]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const programParam = selectedProgram ? `&program_id=${selectedProgram}` : '';
      const url = getWeaveinApiUrl(`/api/work-compliance/cbo/dashboard?cbo_id=${cboId}${programParam}`);

      const response = await authFetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch dashboard: ${response.status}`);
      }

      const result = await response.json();
      setData(result);

      // Fetch trends (last 6 months)
      await fetchTrends();

    } catch (err) {
      console.error('Error fetching dashboard:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrends = async () => {
    try {
      const url = getWeaveinApiUrl(`/api/work-compliance/cbo/trends?cbo_id=${cboId}&months=6`);

      const response = await authFetch(url);

      if (response.ok) {
        const result = await response.json();
        setTrends(result.trends || []);
      }
    } catch (err) {
      console.error('Error fetching trends:', err);
    }
  };

  const fetchVerificationQueue = async () => {
    try {
      setQueueLoading(true);
      const url = getWeaveinApiUrl(
        `/api/work-compliance/cbo/verification-queue?cbo_id=${cboId}&verification_status=${queueFilter}&limit=50`
      );

      const response = await authFetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch queue: ${response.status}`);
      }

      const result = await response.json();
      setSubmissions(result.results || []);
    } catch (err) {
      console.error('Error fetching verification queue:', err);
    } finally {
      setQueueLoading(false);
    }
  };

  const handleVerify = async (submissionId: number, result: string, notes: string = '') => {
    setVerifying(true);
    try {
      const url = getWeaveinApiUrl('/api/work-compliance/cbo/verify');

      const response = await authFetch(url, {
        method: 'POST',
        jsonBody: {
          work_activity_log_id: submissionId,
          verification_result: result,
          verification_notes: notes,
          verification_method: 'DOCUMENT_REVIEW'
        }
      });

      if (!response.ok) {
        throw new Error(`Verification failed: ${response.status}`);
      }

      const resultData = await response.json();

      if (resultData.milestones_triggered?.length > 0) {
        alert(`Verified! Member earned ${resultData.milestones_triggered.length} milestone(s) and $${resultData.total_member_payout.toFixed(2)}`);
      }

      setSelectedSubmission(null);
      await fetchVerificationQueue();
      // Refresh dashboard counts too
      await fetchDashboardData();
    } catch (err) {
      console.error('Error verifying submission:', err);
      alert(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboardData();
    if (activeTab === 'verification') {
      await fetchVerificationQueue();
    }
    setRefreshing(false);
  };

  const exportReport = () => {
    if (!data) return;

    const csvContent = [
      ['CBO Compliance Report'],
      [`Generated: ${fmt.dateTime(new Date().toISOString())}`],
      [`Period: ${getMonthName(data.current_month)} ${data.current_year}`],
      [''],
      ['Metric', 'Value'],
      ['Total Members', data.total_members],
      ['Compliant Members', data.compliant_count],
      ['Compliance Rate', `${data.compliance_rate}%`],
      ['At-Risk Members', data.at_risk_count],
      ['Pending Verifications', data.pending_verifications],
      [''],
      ['At-Risk Members Detail:'],
      ['Member Name', 'Hours Submitted', 'Hours Needed', 'Status'],
      ...data.at_risk_members.map(m => [
        m.member_name,
        m.hours_submitted,
        m.hours_needed,
        m.status || 'NO_SUBMISSION'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `compliance-report-${data.current_month}-${data.current_year}.csv`;
    a.click();
  };

  const getMonthName = (month: number) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[month - 1];
  };

  const getComplianceColor = (rate: number) => {
    if (rate >= 90) return 'text-green-600';
    if (rate >= 75) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getComplianceBgColor = (rate: number) => {
    if (rate >= 90) return 'bg-green-50 border-green-200';
    if (rate >= 75) return 'bg-yellow-50 border-yellow-200';
    return 'bg-red-50 border-red-200';
  };

  const getDaysAgo = (dateString: string | null) => {
    if (!dateString) return 'Unknown';
    const days = Math.floor((Date.now() - new Date(dateString).getTime()) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    return `${days} days ago`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <Card className="p-6 border-l-4 border-amber-500 bg-amber-50">
          <div className="flex items-start text-amber-800">
            <AlertTriangle className="mr-3 mt-0.5 flex-shrink-0" size={24} />
            <div>
              <h3 className="font-semibold text-lg mb-1">{t('workcompliance.work_compliance_api_not_available', 'Work Compliance API Not Available')}</h3>
              <p className="text-sm mb-2">
                The work compliance backend needs to be deployed. {error ? `Error: ${error}` : 'No data available.'}
              </p>
              <p className="text-xs text-amber-600">
                {t('workcompliance.deploy_weaveinapi_on_render_with', 'Deploy weavein-api on Render with the work_compliance router to enable this feature.')}
              </p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const previousMonth = trends.length > 1 ? trends[trends.length - 2] : null;
  const trendDirection = previousMonth
    ? data.compliance_rate - previousMonth.compliance_rate
    : 0;

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[13px] text-ink-500">
          {getMonthName(data.current_month)} {data.current_year}
          {selectedProgram && ' - Program Specific View'}
        </p>
        <div className="flex gap-2">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            {t('workcompliance.refresh', 'Refresh')}
          </button>
          <button
            onClick={exportReport}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Download size={16} />
            {t('workcompliance.export_report', 'Export Report')}
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Total Members */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-2">
            <Users className="text-blue-600" size={24} />
            <span className="text-3xl font-bold">{data.total_members}</span>
          </div>
          <p className="text-gray-600">{t('workcompliance.total_members', 'Total Members')}</p>
          <p className="text-xs text-gray-500 mt-1">with work requirements</p>
        </Card>

        {/* Compliance Rate */}
        <Card className={`p-6 ${getComplianceBgColor(data.compliance_rate)}`}>
          <div className="flex items-center justify-between mb-2">
            <CheckCircle2 className={getComplianceColor(data.compliance_rate)} size={24} />
            <div className="text-right">
              <span className="text-3xl font-bold">{data.compliance_rate.toFixed(1)}%</span>
              {trendDirection !== 0 && (
                <div className={`flex items-center justify-end gap-1 text-sm ${
                  trendDirection > 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {trendDirection > 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                  {Math.abs(trendDirection).toFixed(1)}%
                </div>
              )}
            </div>
          </div>
          <p className="text-gray-600">{t('workcompliance.compliance_rate', 'Compliance Rate')}</p>
          <p className="text-xs text-gray-500 mt-1">
            {data.compliant_count} of {data.total_members} compliant
          </p>
        </Card>

        {/* At-Risk Members */}
        <Card className={`p-6 ${data.at_risk_count > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50'}`}>
          <div className="flex items-center justify-between mb-2">
            <AlertTriangle className={data.at_risk_count > 0 ? 'text-red-600' : 'text-gray-400'} size={24} />
            <span className="text-3xl font-bold">{data.at_risk_count}</span>
          </div>
          <p className="text-gray-600">{t('workcompliance.atrisk_members', 'At-Risk Members')}</p>
          <p className="text-xs text-gray-500 mt-1">need immediate attention</p>
        </Card>

        {/* Pending Verifications - clickable to switch tab */}
        <Card
          className={`p-6 cursor-pointer hover:shadow-md transition ${data.pending_verifications > 0 ? 'bg-yellow-50 border-yellow-200' : 'bg-gray-50'}`}
          onClick={() => setActiveTab('verification')}
        >
          <div className="flex items-center justify-between mb-2">
            <Clock className={data.pending_verifications > 0 ? 'text-yellow-600' : 'text-gray-400'} size={24} />
            <span className="text-3xl font-bold">{data.pending_verifications}</span>
          </div>
          <p className="text-gray-600">{t('workcompliance.pending_verifications', 'Pending Verifications')}</p>
          <p className="text-xs text-blue-600 mt-1 hover:underline">
            {t('workcompliance.review_queue', 'Review queue')} &rarr;
          </p>
        </Card>
      </div>

      {/* Tab Bar */}
      <div className="flex gap-1 border-b">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-6 py-3 font-semibold text-sm border-b-2 transition ${
            activeTab === 'overview'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          {t('workcompliance.overview', 'Overview')}
        </button>
        <button
          onClick={() => setActiveTab('verification')}
          className={`px-6 py-3 font-semibold text-sm border-b-2 transition flex items-center gap-2 ${
            activeTab === 'verification'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          {t('workcompliance.verification_queue', 'Verification Queue')}
          {data.pending_verifications > 0 && (
            <Badge className="bg-red-500 text-white text-xs px-2 py-0.5">
              {data.pending_verifications}
            </Badge>
          )}
        </button>
      </div>

      {/* ===== OVERVIEW TAB ===== */}
      {activeTab === 'overview' && (
        <>
          {/* Compliance Trend Chart */}
          {trends.length > 0 && (
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <TrendingUp />
                6-Month Compliance Trend
              </h2>
              <div className="flex items-end justify-between h-48 gap-2">
                {trends.map((trend, index) => {
                  const height = (trend.compliance_rate / 100) * 100;
                  const color = trend.compliance_rate >= 90
                    ? 'bg-green-500'
                    : trend.compliance_rate >= 75
                    ? 'bg-yellow-500'
                    : 'bg-red-500';

                  return (
                    <div key={index} className="flex-1 flex flex-col items-center">
                      <div className="w-full flex items-end justify-center" style={{ height: '160px' }}>
                        <div
                          className={`w-full ${color} rounded-t transition-all hover:opacity-80 relative group`}
                          style={{ height: `${height}%` }}
                        >
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                            {trend.compliant_count}/{trend.total_members} ({trend.compliance_rate.toFixed(1)}%)
                          </div>
                        </div>
                      </div>
                      <div className="text-center mt-2">
                        <p className="text-xs font-semibold">{getMonthName(trend.month)}</p>
                        <p className="text-xs text-gray-500">'{trend.year.toString().slice(-2)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 flex items-center justify-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-500 rounded"></div>
                  <span>&ge;90% Excellent</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                  <span>75-89% Good</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-red-500 rounded"></div>
                  <span>&lt;75% Needs Attention</span>
                </div>
              </div>
            </Card>
          )}

          {/* At-Risk Members List */}
          {data.at_risk_members.length > 0 && (
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <AlertTriangle className="text-red-600" />
                  At-Risk Members ({data.at_risk_members.length})
                </h2>
                <span className="text-sm text-gray-600">{t('workcompliance.require_immediate_outreach', 'Require immediate outreach')}</span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold">{t('workcompliance.member_name', 'Member Name')}</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold">{t('workcompliance.hours_submitted', 'Hours Submitted')}</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold">{t('workcompliance.hours_needed', 'Hours Needed')}</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">{t('workcompliance.status', 'Status')}</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">{t('workcompliance.action', 'Action')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {data.at_risk_members.map((member) => (
                      <tr key={member.member_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-semibold">{member.member_name}</p>
                            {member.days_remaining !== undefined && (
                              <p className="text-xs text-red-600">
                                {member.days_remaining} days remaining
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="font-semibold">{member.hours_submitted}</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="font-semibold text-red-600">+{member.hours_needed}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge className={
                            member.status === 'NO_SUBMISSION'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }>
                            {member.status === 'NO_SUBMISSION' ? 'No Submission' : 'Insufficient Hours'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => window.location.href = `/members/${member.member_id}/contact`}
                            className="text-blue-600 hover:underline text-sm"
                          >
                            {t('workcompliance.contact_member', 'Contact Member')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Quick Actions */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-4">{t('workcompliance.quick_actions', 'Quick Actions')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={() => setActiveTab('verification')}
                className="p-4 border-2 border-blue-200 rounded-lg hover:bg-blue-50 transition text-left"
              >
                <FileText className="text-blue-600 mb-2" size={24} />
                <p className="font-semibold">{t('workcompliance.verify_submissions', 'Verify Submissions')}</p>
                <p className="text-sm text-gray-600">{data.pending_verifications} pending</p>
              </button>

              <button
                onClick={() => {
                  const memberIds = data.at_risk_members.map(m => m.member_id).join(',');
                  window.location.href = `/communications/bulk-message?member_ids=${memberIds}`;
                }}
                disabled={data.at_risk_members.length === 0}
                className="p-4 border-2 border-orange-200 rounded-lg hover:bg-orange-50 transition text-left disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <AlertTriangle className="text-orange-600 mb-2" size={24} />
                <p className="font-semibold">{t('workcompliance.contact_atrisk', 'Contact At-Risk')}</p>
                <p className="text-sm text-gray-600">{data.at_risk_members.length} members</p>
              </button>

              <button
                onClick={exportReport}
                className="p-4 border-2 border-green-200 rounded-lg hover:bg-green-50 transition text-left"
              >
                <Download className="text-green-600 mb-2" size={24} />
                <p className="font-semibold">{t('workcompliance.download_report', 'Download Report')}</p>
                <p className="text-sm text-gray-600">{t('workcompliance.csv_format', 'CSV format')}</p>
              </button>
            </div>
          </Card>

          {/* Excellence Recognition */}
          {data.compliance_rate >= 90 && (
            <Card className="p-6 bg-gradient-to-r from-green-50 to-blue-50 border-green-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="text-5xl">&#127942;</div>
                  <div>
                    <p className="text-xl font-bold text-green-800">{t('workcompliance.excellence_achievement', 'Excellence Achievement!')}</p>
                    <p className="text-gray-700 mt-1">
                      Your team has maintained a {data.compliance_rate.toFixed(1)}% compliance rate.
                      You may be eligible for the Program Compliance Excellence milestone ($1,000 bonus).
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </>
      )}

      {/* ===== VERIFICATION QUEUE TAB ===== */}
      {activeTab === 'verification' && (
        <>
          {/* Filter Tabs */}
          <div className="flex gap-2">
            {['PENDING', 'VERIFIED', 'REJECTED'].map(status => (
              <button
                key={status}
                onClick={() => setQueueFilter(status)}
                className={`px-4 py-2 rounded-lg font-semibold ${
                  queueFilter === status
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {status}
                {status === 'PENDING' && data.pending_verifications > 0 && (
                  <Badge className="ml-2 bg-red-500 text-white">{data.pending_verifications}</Badge>
                )}
              </button>
            ))}
          </div>

          {/* Queue List */}
          {queueLoading ? (
            <div className="flex justify-center items-center py-12">
              <LoadingSpinner size="large" />
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {submissions.length === 0 ? (
                <Card className="p-8 text-center text-gray-500">
                  <p>No submissions in {queueFilter.toLowerCase()} status</p>
                </Card>
              ) : (
                submissions.map(submission => (
                  <Card
                    key={submission.id}
                    className={`p-6 hover:shadow-lg transition cursor-pointer ${
                      selectedSubmission?.id === submission.id ? 'ring-2 ring-blue-500' : ''
                    }`}
                    onClick={() => setSelectedSubmission(submission)}
                  >
                    <div className="flex items-start justify-between">
                      {/* Left Section - Member Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <User className="text-gray-600" size={20} />
                          <h3 className="font-semibold text-lg">{submission.member_name}</h3>
                          <Badge className={
                            submission.meets_requirement
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }>
                            {submission.total_hours} hrs
                          </Badge>
                        </div>

                        <div className="grid grid-cols-3 gap-4 mt-4">
                          <div>
                            <p className="text-sm text-gray-600">{t('workcompliance.period', 'Period')}</p>
                            <p className="font-semibold">
                              {getMonthName(submission.month)} {submission.year}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">{t('workcompliance.submitted', 'Submitted')}</p>
                            <p className="font-semibold">{getDaysAgo(submission.submitted_date)}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">{t('workcompliance.documents', 'Documents')}</p>
                            <div className="flex items-center gap-1">
                              <FileText size={16} className={submission.has_documents ? 'text-green-600' : 'text-gray-400'} />
                              <span className="font-semibold">{submission.document_count}</span>
                            </div>
                          </div>
                        </div>

                        {/* Hours Breakdown */}
                        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-2">
                          {submission.employment_hours > 0 && (
                            <div className="text-sm">
                              <Briefcase className="inline mr-1" size={14} />
                              {t('workcompliance.employment', 'Employment:')} <strong>{submission.employment_hours}h</strong>
                            </div>
                          )}
                          {submission.education_hours > 0 && (
                            <div className="text-sm">
                              {t('workcompliance.education', 'Education:')} <strong>{submission.education_hours}h</strong>
                            </div>
                          )}
                          {submission.volunteer_hours > 0 && (
                            <div className="text-sm">
                              {t('workcompliance.volunteer', 'Volunteer:')} <strong>{submission.volunteer_hours}h</strong>
                            </div>
                          )}
                          {submission.job_training_hours > 0 && (
                            <div className="text-sm">
                              {t('workcompliance.training', 'Training:')} <strong>{submission.job_training_hours}h</strong>
                            </div>
                          )}
                        </div>

                        {submission.employer_name && (
                          <div className="mt-2 text-sm text-gray-600">
                            {t('workcompliance.employer', 'Employer:')} <span className="font-medium">{submission.employer_name}</span>
                          </div>
                        )}
                      </div>

                      {/* Right Section - Actions */}
                      {queueFilter === 'PENDING' && (
                        <div className="flex flex-col gap-2 ml-4">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (confirm(`Approve ${submission.total_hours} hours for ${submission.member_name}?`)) {
                                handleVerify(submission.id, 'APPROVED');
                              }
                            }}
                            disabled={verifying}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 flex items-center gap-2"
                          >
                            <CheckCircle2 size={16} />
                            {t('workcompliance.approve', 'Approve')}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const reason = prompt('Rejection reason:');
                              if (reason) {
                                handleVerify(submission.id, 'REJECTED', reason);
                              }
                            }}
                            disabled={verifying}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 flex items-center gap-2"
                          >
                            <XCircle size={16} />
                            {t('workcompliance.reject', 'Reject')}
                          </button>
                        </div>
                      )}

                      {queueFilter !== 'PENDING' && (
                        <Badge className={
                          queueFilter === 'VERIFIED'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }>
                          {queueFilter}
                        </Badge>
                      )}
                    </div>
                  </Card>
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CBOComplianceDashboard;
